import React from 'react';
import {Link} from 'react-router-dom';
import {useTranslation} from 'react-i18next';
import {WarningCircle, CheckCircle, PauseCircle, Trash} from '@phosphor-icons/react';

function getInitials(name) {
    if (!name) return '?';
    return name.split(' ').slice(0, 2).map(w => w[0]).join('').toUpperCase();
}

function formatCurrency(amount, currency) {
    if (amount == null) return '—';
    const formatted = new Intl.NumberFormat('es-AR', {
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
    }).format(amount);
    return currency ? `${currency} ${formatted}` : formatted;
}

function getBudgetStatus(spend, budget) {
    if (!budget || budget === 0) return 'gray';
    const ratio = spend / budget;
    if (ratio > 1.05) return 'red';
    if (ratio > 0.85) return 'yellow';
    return 'green';
}

export function ClientCard({client, canDelete = false, onDelete}) {
    const {t} = useTranslation();

    const initials = getInitials(client.name);
    const buCount = client.business_units_count || 0;
    const buLabel = buCount === 1
        ? t('client_cards.business_units_count_one', {count: buCount})
        : t('client_cards.business_units_count', {count: buCount});

    const hasSummary = client.total_budget != null && (buCount > 0 || client.total_budget > 0);
    const budgetDot = hasSummary ? getBudgetStatus(client.total_spend || 0, client.total_budget) : 'gray';
    const alertsCount = client.alerts_count || 0;
    const goalsMet = client.goals_met || 0;
    const goalsTotal = client.goals_total || 0;
    const goalsDot = goalsTotal === 0 ? 'gray' : (goalsMet >= goalsTotal ? 'green' : (goalsMet > 0 ? 'yellow' : 'red'));

    return (
        <article className="client-card">
            <Link className="client-card-main" to={`/client/${client.id}`}>
                <div className="client-card-header">
                    <div className="client-card-initial">{initials}</div>
                    <div className="client-card-info">
                        <div className="client-card-name">
                            {client.name}
                            {!client.active && (
                                <span className="client-card-badge-inactive">
                                    <PauseCircle size={12} weight="fill"/>
                                    {t('client_cards.inactive')}
                                </span>
                            )}
                        </div>
                        <div className="client-card-subtitle">{buLabel}</div>
                    </div>
                </div>

                {hasSummary && (
                    <div className="client-card-indicators">
                        <div className="client-card-indicator">
                            <div className={`client-card-indicator-dot ${budgetDot}`}/>
                            <div className="client-card-indicator-content">
                                <span className="client-card-indicator-label">{t('client_cards.spend')} / {t('client_cards.budget')}</span>
                                <span className="client-card-indicator-value">
                                    {formatCurrency(client.total_spend, client.currency)} / {formatCurrency(client.total_budget, client.currency)}
                                </span>
                            </div>
                        </div>

                        {goalsTotal > 0 && (
                            <div className="client-card-indicator">
                                <div className={`client-card-indicator-dot ${goalsDot}`}/>
                                <div className="client-card-indicator-content">
                                    <span className="client-card-indicator-label">{t('client_cards.goals')}</span>
                                    <span className="client-card-indicator-value">
                                        {t('client_cards.goals_met', {met: goalsMet, total: goalsTotal})}
                                    </span>
                                </div>
                            </div>
                        )}
                    </div>
                )}

                <div className={`client-card-alerts ${alertsCount > 0 ? 'has-alerts' : 'no-alerts'}`}>
                    {alertsCount > 0 ? (
                        <>
                            <WarningCircle size={16} weight="fill"/>
                            {alertsCount === 1
                                ? t('client_cards.alerts_one', {count: alertsCount})
                                : t('client_cards.alerts', {count: alertsCount})
                            }
                        </>
                    ) : (
                        <>
                            <CheckCircle size={16}/>
                            {t('client_cards.no_alerts')}
                        </>
                    )}
                </div>
            </Link>

            {canDelete && (
                <button
                    type="button"
                    className="client-card-delete-btn"
                    onClick={onDelete}
                    title={t('client_cards.delete_client')}
                    aria-label={t('client_cards.delete_client')}
                >
                    <Trash size={16} weight="bold"/>
                </button>
            )}
        </article>
    );
}
